import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, Loader2, Plus, Tag, FileText } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  RegistrationCart,
  PixDialog,
  BoletoDialog,
  ThankYouScreen,
  PriceLots,
} from '@/components/event';
import { encryptCardForPagSeguro } from '@/lib/pagseguro';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getCartKey = (eventId) => `maxx4s_cart_${eventId}`;
const generateCartItemId = () => `cart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const DEFAULT_BILLING_DATA = {
  usar_dados_faturamento: false,
  faturamento_tipo: 'pf',
  faturamento_razao_social: '',
  faturamento_cnpj: '',
  faturamento_nome: '',
  faturamento_cpf: '',
};

const ESTADOS_BRASIL = [
  { sigla: 'AC', nome: 'Acre' }, { sigla: 'AL', nome: 'Alagoas' }, { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' }, { sigla: 'BA', nome: 'Bahia' }, { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' }, { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' }, { sigla: 'MA', nome: 'Maranhão' }, { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' }, { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' }, { sigla: 'PB', nome: 'Paraíba' }, { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' }, { sigla: 'PI', nome: 'Piauí' }, { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' }, { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' }, { sigla: 'RR', nome: 'Roraima' }, { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' }, { sigla: 'SE', nome: 'Sergipe' }, { sigla: 'TO', nome: 'Tocantins' },
];

const EventDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [categories, setCategories] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pixDialogOpen, setPixDialogOpen] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [boletoDialogOpen, setBoletoDialogOpen] = useState(false);
  const [boletoData, setBoletoData] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [regulamentoDialogOpen, setRegulamentoDialogOpen] = useState(false);
  const [registrationIds, setRegistrationIds] = useState([]);

  const [registrationCart, setRegistrationCart] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [cardForm, setCardForm] = useState({
    holder_name: '',
    number: '',
    expiry: '',
    security_code: '',
    installments: 1,
  });

  const [billingData, setBillingData] = useState(DEFAULT_BILLING_DATA);
  const [billingParticipantIndex, setBillingParticipantIndex] = useState(0);
  const [eventType, setEventType] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const emptyFormData = {
    nome: '', sobrenome: '', email: '', telefone: '', whatsapp: '',
    cpf: '', rg: '', data_nascimento: '', sexo: '',
    profissao: '', telefone_emergencia: '', contato_emergencia: '',
    rua: '', numero: '', complemento: '', bairro: '', cidade: '', estado: 'MG', cep: '', pais: 'Brasil',
    percurso: '', categoria: '', tamanho_camisa: 'M', equipe: '', observacoes: '',
    custom_fields: {},
    // Itens opcionais selecionados
    selected_optional_items: [],
    // Campos do responsável (para menores de 18 anos)
    nome_responsavel: '', cpf_responsavel: '', telefone_responsavel: '',
    aceite_termos: false, aceite_responsabilidade: false, aceite_imagem: false, aceite_marketing: false,
  };
  const [formData, setFormData] = useState(emptyFormData);

  // Função para calcular idade
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const participantAge = calculateAge(formData.data_nascimento);
  const isMinor = participantAge !== null && participantAge < 18;

  // Helper para classes de erro
  const getInputClass = (fieldName, baseClass = '') => {
    const errorClass = fieldErrors[fieldName] ? 'border-red-500 bg-red-50' : 'border-border';
    return `${baseClass} ${errorClass}`.trim();
  };

  // Limpar erro quando campo é preenchido
  const clearFieldError = (fieldName) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const openRegulamentoFile = () => {
    if (!event?.regulamento_file_url) return;

    try {
      const [meta, base64Data] = event.regulamento_file_url.split(',');
      const mimeMatch = meta.match(/data:(.*?);base64/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'application/pdf';
      const binary = atob(base64Data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      const newWindow = window.open(blobUrl, '_blank');
      if (!newWindow) {
        toast({ title: 'Erro', description: 'Não foi possível abrir o PDF. Verifique seu bloqueador de pop-up.', variant: 'destructive' });
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('Erro ao abrir regulamento PDF:', error);
      toast({ title: 'Erro', description: 'Não foi possível abrir o regulamento em PDF.', variant: 'destructive' });
    }
  };

  // Gerenciar itens opcionais selecionados
  const handleOptionalItemChange = (item, quantity) => {
    const existing = formData.selected_optional_items.find(i => i.item_id === item.id);
    
    if (quantity <= 0) {
      // Remover item
      setFormData({
        ...formData,
        selected_optional_items: formData.selected_optional_items.filter(i => i.item_id !== item.id)
      });
    } else {
      // Adicionar ou atualizar item
      if (existing) {
        setFormData({
          ...formData,
          selected_optional_items: formData.selected_optional_items.map(i => 
            i.item_id === item.id ? { ...i, quantity: Math.min(quantity, item.max_quantity) } : i
          )
        });
      } else {
        setFormData({
          ...formData,
          selected_optional_items: [
            ...formData.selected_optional_items,
            { item_id: item.id, name: item.name, quantity: Math.min(quantity, item.max_quantity), price: item.price }
          ]
        });
      }
    }
  };

  // Calcular total dos itens opcionais
  const optionalItemsTotal = formData.selected_optional_items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Formatters
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers ? `(${numbers}` : '';
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  const handleMaskedInput = (e, formatter, fieldName) => {
    const formatted = formatter(e.target.value);
    setFormData({ ...formData, [fieldName]: formatted });
  };

  // Cart management
  const loadCartFromStorage = useCallback(() => {
    if (!id) return;
    try {
      const stored = localStorage.getItem(getCartKey(id));
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.registrations?.length > 0) {
          setRegistrationCart(parsed.registrations);
          setBillingData({ ...DEFAULT_BILLING_DATA, ...(parsed.billingData || {}) });
          setPaymentMethod(parsed.paymentMethod || 'pix');
          setShowCart(true);
        }
      }
    } catch (e) { console.error('Error loading cart:', e); }
  }, [id]);

  const saveCartToStorage = useCallback((registrations, billing, payment) => {
    if (!id) return;
    try {
      localStorage.setItem(getCartKey(id), JSON.stringify({
        registrations, billingData: billing, paymentMethod: payment,
      }));
    } catch (e) { console.error('Error saving cart:', e); }
  }, [id]);

  const clearCart = useCallback(() => {
    setRegistrationCart([]);
    setBillingData(DEFAULT_BILLING_DATA);
    setPaymentMethod('pix');
    setCardForm({ holder_name: '', number: '', expiry: '', security_code: '', installments: 1 });
    setBillingParticipantIndex(0);
    if (id) localStorage.removeItem(getCartKey(id));
  }, [id]);

  const addToCart = () => {
    const errors = {};
    const missingFields = [];
    
    // Validar campos obrigatórios básicos
    if (!formData.nome) { errors.nome = true; missingFields.push('Nome'); }
    if (!formData.sobrenome) { errors.sobrenome = true; missingFields.push('Sobrenome'); }
    if (!formData.email) { errors.email = true; missingFields.push('Email'); }
    if (!formData.cpf) { errors.cpf = true; missingFields.push('CPF'); }
    if (!formData.percurso) { errors.percurso = true; missingFields.push('Percurso'); }
    if (!formData.categoria) { errors.categoria = true; missingFields.push('Categoria'); }
    
    // Validar aceites
    if (!formData.aceite_termos) { errors.aceite_termos = true; missingFields.push('Aceite dos Termos'); }
    if (!formData.aceite_responsabilidade) { errors.aceite_responsabilidade = true; missingFields.push('Declaração de Responsabilidade'); }
    
    // Validar campos do responsável para menores de idade
    if (isMinor) {
      if (!formData.nome_responsavel) { errors.nome_responsavel = true; missingFields.push('Nome do Responsável'); }
      if (!formData.cpf_responsavel) { errors.cpf_responsavel = true; missingFields.push('CPF do Responsável'); }
      if (!formData.telefone_responsavel) { errors.telefone_responsavel = true; missingFields.push('Telefone do Responsável'); }
      
      // Validar formato CPF do responsável
      if (formData.cpf_responsavel) {
        const cpfNumbers = formData.cpf_responsavel.replace(/\D/g, '');
        if (cpfNumbers.length !== 11) {
          errors.cpf_responsavel = true;
          missingFields.push('CPF do Responsável (formato inválido)');
        }
      }
    }
    
    // Validar campos personalizados obrigatórios
    if (eventType?.custom_fields) {
      for (const field of eventType.custom_fields) {
        if (field.required && !formData.custom_fields?.[field.field_key]) {
          errors[`custom_${field.field_key}`] = true;
          missingFields.push(field.name);
        }
      }
    }
    
    // Se houver erros, mostrar e não prosseguir
    setFieldErrors(errors);
    
    if (missingFields.length > 0) {
      toast({ 
        title: 'Campos obrigatórios não preenchidos', 
        description: `Por favor, preencha: ${missingFields.join(', ')}`, 
        variant: 'destructive' 
      });
      return;
    }

    const newItem = { 
      ...formData, 
      cartItemId: generateCartItemId(), 
      addedAt: new Date().toISOString(),
      optional_items: formData.selected_optional_items,
      optional_items_total: optionalItemsTotal
    };

    if (editingIndex !== null) {
      const newCart = [...registrationCart];
      newCart[editingIndex] = newItem;
      setRegistrationCart(newCart);
      saveCartToStorage(newCart, billingData, paymentMethod);
      setEditingIndex(null);
      toast({ title: 'Participante atualizado', description: `${formData.nome} ${formData.sobrenome} foi atualizado no carrinho.` });
    } else {
      const newCart = [...registrationCart, newItem];
      setRegistrationCart(newCart);
      saveCartToStorage(newCart, billingData, paymentMethod);
      toast({ title: 'Adicionado ao carrinho', description: `${formData.nome} ${formData.sobrenome} foi adicionado.` });
    }
    setFormData(emptyFormData);
    setShowCart(true);
  };

  const removeFromCart = (index) => {
    const newCart = registrationCart.filter((_, i) => i !== index);
    setRegistrationCart(newCart);
    saveCartToStorage(newCart, billingData, paymentMethod);
    if (billingParticipantIndex >= newCart.length) setBillingParticipantIndex(Math.max(0, newCart.length - 1));
    toast({ title: 'Removido', description: 'Participante removido do carrinho.' });
  };

  const editCartItem = (index) => {
    const item = registrationCart[index];
    const { cartItemId, addedAt, ...itemData } = item;
    setFormData(itemData);
    setEditingIndex(index);
    setShowCart(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCurrentPrice = () => {
    if (event?.current_price) return event.current_price;
    return event?.price || 0;
  };

  // parse a date string like "YYYY-MM-DD" into a local Date object without
  // shifting one day due to timezone differences.  JS treats bare
  // YYYY-MM-DD strings as UTC which then get converted to local time and can
  // show the previous day when the timezone is negative (e.g. Brazil -3).
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return new Date(dateStr);
    const [year, month, day] = parts.map(Number);
    return new Date(year, month - 1, day);
  };

  const formatEventDate = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return '';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API}/events/${id}/details`);
        setEvent(response.data.event);
        setCategories(response.data.categories || []);
        setRoutes(response.data.routes || []);
        
        // Buscar tipo de prova para campos dinâmicos
        if (response.data.event?.category) {
          try {
            const typesRes = await axios.get(`${API}/event-types`);
            const eventTypeData = typesRes.data.find(et => et.name === response.data.event.category);
            setEventType(eventTypeData || null);
          } catch (e) { console.error('Error fetching event types:', e); }
        }
      } catch (error) {
        toast({ title: 'Erro', description: 'Não foi possível carregar o evento.', variant: 'destructive' });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id, navigate, toast]);

  useEffect(() => {
    if (!loading && event) loadCartFromStorage();
  }, [loading, event, loadCartFromStorage]);

  const handleCheckout = async () => {
    if (registrationCart.length === 0) {
      toast({ title: 'Carrinho vazio', description: 'Adicione ao menos um participante.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const participants = registrationCart.map(reg => {
        const { cartItemId, addedAt, ...participantData } = reg;
        return participantData;
      });

      const encryptedCard = paymentMethod === 'credit_card'
        ? await encryptCardForPagSeguro(API, cardForm)
        : null;

      const response = await axios.post(`${API}/registrations/batch`, {
        event_id: id,
        payment_method: paymentMethod,
        card: encryptedCard,
        participants,
        billing_participant_index: billingParticipantIndex,
        usar_dados_faturamento: billingData.usar_dados_faturamento,
        faturamento_tipo: billingData.faturamento_tipo,
        faturamento_razao_social: billingData.faturamento_razao_social,
        faturamento_cnpj: billingData.faturamento_cnpj,
        faturamento_nome: billingData.faturamento_nome,
        faturamento_cpf: billingData.faturamento_cpf,
      });

      const data = response.data;

      if (paymentMethod === 'pix') {
        if (data.pix_code || data.qr_code_base64) {
          setPixData({ pix_code: data.pix_code, qr_code_base64: data.qr_code_base64 });
          setPixDialogOpen(true);
        } else {
          toast({ title: 'Aviso', description: 'Inscrição criada, mas houve um problema ao gerar o PIX. Verifique seu email.', variant: 'destructive' });
        }
      } else if (paymentMethod === 'boleto') {
        setBoletoData({
          boleto_url: data.boleto_url,
          boleto_pdf_url: data.boleto_pdf_url,
          boleto_barcode: data.boleto_barcode,
          boleto_formatted_barcode: data.boleto_formatted_barcode,
          boleto_due_date: data.boleto_due_date,
        });
        setBoletoDialogOpen(true);
      } else if (paymentMethod === 'credit_card') {
        toast({
          title: data.payment_status === 'paid' ? 'Pagamento aprovado!' : 'Pagamento em análise',
          description: data.payment_status === 'paid'
            ? 'Sua inscrição foi confirmada com cartão de crédito.'
            : 'Sua inscrição foi criada e o pagamento está em análise.',
        });
      }

      setRegistrationIds(data.registration_ids || [data.registration_id]);
      clearCart();
      setShowThankYou(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Não foi possível completar a inscrição.';
      toast({ title: 'Erro', description: errorMsg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  if (showThankYou) {
    return (
      <>
        <ThankYouScreen
          registrationIds={registrationIds}
          onNewRegistration={() => { setShowThankYou(false); setFormData(emptyFormData); setRegistrationIds([]); }}
        />
        <PixDialog open={pixDialogOpen} onOpenChange={setPixDialogOpen} pixData={pixData} />
        <BoletoDialog open={boletoDialogOpen} onOpenChange={setBoletoDialogOpen} boletoData={boletoData} />
      </>
    );
  }

  return (
    <div data-testid="event-detail-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 md:h-[500px]">
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent z-10" />
        <img src={event?.image_url} alt={event?.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative z-20 h-full flex items-end pb-12 px-6 md:px-12 lg:px-24">
          <div>
            <span className="text-xs uppercase tracking-widest font-bold text-accent bg-accent/20 px-3 py-1 rounded-sm">
              {event?.category}
            </span>
            <h1 className="text-4xl md:text-6xl font-heading font-black tracking-tight uppercase text-foreground mt-4">
              {event?.name}
            </h1>
            <div className="flex flex-wrap gap-6 mt-4 text-foreground/80">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-accent" />
                <span>{formatEventDate(event?.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-accent" />
                <span>{event?.location}</span>
              </div>
              { /*
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-accent" />
                <span>{event?.current_participants || 0} / {event?.max_participants} participantes</span>
              </div>
              */ }
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Event Description */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-2xl font-heading font-bold uppercase text-primary mb-4">Sobre o Evento</h2>
              <p className="text-foreground/80 whitespace-pre-line">{event?.description}</p>
            </div>

            {/* Routes */}
            {routes.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-heading font-bold uppercase text-primary mb-4">Percursos Disponíveis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {routes.map((route) => (
                    <div key={route.id} className="p-4 bg-secondary/50 rounded-lg">
                      <p className="font-bold text-foreground">{route.name}</p>
                      <p className="text-sm text-foreground/70">{route.distance_km} km</p>
                      {route.description && <p className="text-xs text-foreground/50 mt-1">{route.description}</p>}
                      {route.map_url && (
                        <a
                          href={route.map_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`map-link-${route.id}`}
                          className="inline-flex items-center gap-1 mt-2 text-sm text-accent hover:text-accent/80 font-bold"
                        >
                          <MapPin className="w-4 h-4" />
                          Ver Mapa do Percurso
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Registration Form */}
            <div data-testid="registration-form" className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-2xl font-heading font-bold uppercase text-primary mb-6">
                {editingIndex !== null ? 'Editar Participante' : 'Adicionar Participante'}
              </h2>

              <div className="space-y-6">
                {/* Personal Data */}
                <div>
                  <h3 className="text-lg font-bold text-primary mb-4 border-b border-border pb-2">Dados Pessoais</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className={`text-xs font-bold uppercase tracking-widest mb-2 ${fieldErrors.nome ? 'text-red-600' : 'text-primary'}`}>Nome *</Label>
                      <Input data-testid="input-nome" value={formData.nome} onChange={(e) => { setFormData({ ...formData, nome: e.target.value }); clearFieldError('nome'); }} className={`bg-white border-2 rounded-sm h-12 px-4 ${fieldErrors.nome ? 'border-red-500 bg-red-50' : 'border-border'}`} />
                      {fieldErrors.nome && <span className="text-xs text-red-600 mt-1">Campo obrigatório</span>}
                    </div>
                    <div>
                      <Label className={`text-xs font-bold uppercase tracking-widest mb-2 ${fieldErrors.sobrenome ? 'text-red-600' : 'text-primary'}`}>Sobrenome *</Label>
                      <Input data-testid="input-sobrenome" value={formData.sobrenome} onChange={(e) => { setFormData({ ...formData, sobrenome: e.target.value }); clearFieldError('sobrenome'); }} className={`bg-white border-2 rounded-sm h-12 px-4 ${fieldErrors.sobrenome ? 'border-red-500 bg-red-50' : 'border-border'}`} />
                      {fieldErrors.sobrenome && <span className="text-xs text-red-600 mt-1">Campo obrigatório</span>}
                    </div>
                    <div>
                      <Label className={`text-xs font-bold uppercase tracking-widest mb-2 ${fieldErrors.email ? 'text-red-600' : 'text-primary'}`}>Email *</Label>
                      <Input data-testid="input-email" type="email" value={formData.email} onChange={(e) => { setFormData({ ...formData, email: e.target.value }); clearFieldError('email'); }} className={`bg-white border-2 rounded-sm h-12 px-4 ${fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-border'}`} />
                      {fieldErrors.email && <span className="text-xs text-red-600 mt-1">Campo obrigatório</span>}
                    </div>
                    <div>
                      <Label className={`text-xs font-bold uppercase tracking-widest mb-2 ${fieldErrors.cpf ? 'text-red-600' : 'text-primary'}`}>CPF *</Label>
                      <Input data-testid="input-cpf" value={formData.cpf} onChange={(e) => { handleMaskedInput(e, formatCPF, 'cpf'); clearFieldError('cpf'); }} maxLength={14} className={`bg-white border-2 rounded-sm h-12 px-4 ${fieldErrors.cpf ? 'border-red-500 bg-red-50' : 'border-border'}`} />
                      {fieldErrors.cpf && <span className="text-xs text-red-600 mt-1">Campo obrigatório</span>}
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">RG</Label>
                      <Input data-testid="input-rg" value={formData.rg} onChange={(e) => setFormData({ ...formData, rg: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Data de Nascimento</Label>
                      <Input data-testid="input-data-nascimento" type="date" value={formData.data_nascimento} onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Sexo</Label>
                      <select data-testid="select-sexo" value={formData.sexo} onChange={(e) => setFormData({ ...formData, sexo: e.target.value })} className="w-full bg-white border-2 border-border rounded-sm h-12 px-4">
                        <option value="">Selecione</option>
                        <option value="M">Masculino</option>
                        <option value="F">Feminino</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Telefone *</Label>
                      <Input data-testid="input-telefone" value={formData.telefone} onChange={(e) => handleMaskedInput(e, formatPhone, 'telefone')} maxLength={15} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">WhatsApp</Label>
                      <Input data-testid="input-whatsapp" value={formData.whatsapp} onChange={(e) => handleMaskedInput(e, formatPhone, 'whatsapp')} maxLength={15} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Profissão</Label>
                      <Input data-testid="input-profissao" value={formData.profissao} onChange={(e) => setFormData({ ...formData, profissao: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div>
                  <h3 className="text-lg font-bold text-primary mb-4 border-b border-border pb-2">Contato de Emergência</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Nome do Contato *</Label>
                      <Input data-testid="input-contato-emergencia" value={formData.contato_emergencia} onChange={(e) => setFormData({ ...formData, contato_emergencia: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Telefone de Emergência *</Label>
                      <Input data-testid="input-telefone-emergencia" value={formData.telefone_emergencia} onChange={(e) => handleMaskedInput(e, formatPhone, 'telefone_emergencia')} maxLength={15} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-lg font-bold text-primary mb-4 border-b border-border pb-2">Endereço</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Rua *</Label>
                      <Input data-testid="input-rua" value={formData.rua} onChange={(e) => setFormData({ ...formData, rua: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Número *</Label>
                      <Input data-testid="input-numero" value={formData.numero} onChange={(e) => setFormData({ ...formData, numero: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Complemento</Label>
                      <Input data-testid="input-complemento" value={formData.complemento} onChange={(e) => setFormData({ ...formData, complemento: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Bairro *</Label>
                      <Input data-testid="input-bairro" value={formData.bairro} onChange={(e) => setFormData({ ...formData, bairro: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Cidade *</Label>
                      <Input data-testid="input-cidade" value={formData.cidade} onChange={(e) => setFormData({ ...formData, cidade: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Estado *</Label>
                      <select data-testid="select-estado" value={formData.estado} onChange={(e) => setFormData({ ...formData, estado: e.target.value })} className="w-full bg-white border-2 border-border rounded-sm h-12 px-4">
                        {ESTADOS_BRASIL.map(e => <option key={e.sigla} value={e.sigla}>{e.sigla} - {e.nome}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">CEP *</Label>
                      <Input data-testid="input-cep" value={formData.cep} onChange={(e) => handleMaskedInput(e, formatCEP, 'cep')} maxLength={9} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                  </div>
                </div>

                {/* Guardian Data - Only for minors */}
                {isMinor && (
                  <div data-testid="guardian-section" className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6">
                    <h3 className="text-lg font-bold text-yellow-700 mb-4 border-b border-yellow-300 pb-2">
                      Dados do Responsável (Participante menor de 18 anos)
                    </h3>
                    <p className="text-sm text-yellow-600 mb-4">
                      O participante tem {participantAge} anos. Por ser menor de idade, é obrigatório informar os dados do responsável legal.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className={`text-xs font-bold uppercase tracking-widest mb-2 ${fieldErrors.nome_responsavel ? 'text-red-600' : 'text-yellow-700'}`}>Nome do Responsável *</Label>
                        <Input 
                          data-testid="input-nome-responsavel" 
                          value={formData.nome_responsavel} 
                          onChange={(e) => { setFormData({ ...formData, nome_responsavel: e.target.value }); clearFieldError('nome_responsavel'); }} 
                          className={`bg-white border-2 rounded-sm h-12 px-4 ${fieldErrors.nome_responsavel ? 'border-red-500 bg-red-50' : 'border-yellow-300 focus:border-yellow-500'}`}
                          placeholder="Nome completo do responsável"
                        />
                        {fieldErrors.nome_responsavel && <span className="text-xs text-red-600 mt-1">Campo obrigatório</span>}
                      </div>
                      <div>
                        <Label className={`text-xs font-bold uppercase tracking-widest mb-2 ${fieldErrors.cpf_responsavel ? 'text-red-600' : 'text-yellow-700'}`}>CPF do Responsável *</Label>
                        <Input 
                          data-testid="input-cpf-responsavel" 
                          value={formData.cpf_responsavel} 
                          onChange={(e) => { handleMaskedInput(e, formatCPF, 'cpf_responsavel'); clearFieldError('cpf_responsavel'); }} 
                          maxLength={14} 
                          className={`bg-white border-2 rounded-sm h-12 px-4 ${fieldErrors.cpf_responsavel ? 'border-red-500 bg-red-50' : 'border-yellow-300 focus:border-yellow-500'}`}
                          placeholder="000.000.000-00"
                        />
                        {fieldErrors.cpf_responsavel && <span className="text-xs text-red-600 mt-1">Campo obrigatório</span>}
                      </div>
                      <div>
                        <Label className={`text-xs font-bold uppercase tracking-widest mb-2 ${fieldErrors.telefone_responsavel ? 'text-red-600' : 'text-yellow-700'}`}>Telefone do Responsável *</Label>
                        <Input 
                          data-testid="input-telefone-responsavel" 
                          value={formData.telefone_responsavel} 
                          onChange={(e) => { handleMaskedInput(e, formatPhone, 'telefone_responsavel'); clearFieldError('telefone_responsavel'); }} 
                          maxLength={15} 
                          className={`bg-white border-2 rounded-sm h-12 px-4 ${fieldErrors.telefone_responsavel ? 'border-red-500 bg-red-50' : 'border-yellow-300 focus:border-yellow-500'}`}
                          placeholder="(00) 00000-0000"
                        />
                        {fieldErrors.telefone_responsavel && <span className="text-xs text-red-600 mt-1">Campo obrigatório</span>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Event Data */}
                <div>
                  <h3 className="text-lg font-bold text-primary mb-4 border-b border-border pb-2">Dados do Evento</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className={`text-xs font-bold uppercase tracking-widest mb-2 ${fieldErrors.percurso ? 'text-red-600' : 'text-primary'}`}>Percurso *</Label>
                      <select data-testid="select-percurso" value={formData.percurso} onChange={(e) => { setFormData({ ...formData, percurso: e.target.value }); clearFieldError('percurso'); }} className={`w-full bg-white border-2 rounded-sm h-12 px-4 ${fieldErrors.percurso ? 'border-red-500 bg-red-50' : 'border-border'}`}>
                        <option value="">Selecione</option>
                        {routes.map(route => <option key={route.id} value={route.name}>{route.name} ({route.distance_km} km)</option>)}
                      </select>
                      {fieldErrors.percurso && <span className="text-xs text-red-600 mt-1">Campo obrigatório</span>}
                    </div>
                    <div>
                      <Label className={`text-xs font-bold uppercase tracking-widest mb-2 ${fieldErrors.categoria ? 'text-red-600' : 'text-primary'}`}>Categoria *</Label>
                      <select data-testid="select-categoria" value={formData.categoria} onChange={(e) => { setFormData({ ...formData, categoria: e.target.value }); clearFieldError('categoria'); }} className={`w-full bg-white border-2 rounded-sm h-12 px-4 ${fieldErrors.categoria ? 'border-red-500 bg-red-50' : 'border-border'}`}>
                        <option value="">Selecione</option>
                        {categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                      </select>
                      {fieldErrors.categoria && <span className="text-xs text-red-600 mt-1">Campo obrigatório</span>}
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Tamanho Camisa *</Label>
                      <select data-testid="select-tamanho-camisa" value={formData.tamanho_camisa} onChange={(e) => setFormData({ ...formData, tamanho_camisa: e.target.value })} className="w-full bg-white border-2 border-border rounded-sm h-12 px-4">
                        {['PP', 'P', 'M', 'G', 'GG', 'XG', 'XXG'].map(size => <option key={size} value={size}>{size}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Equipe</Label>
                      <Input data-testid="input-equipe" value={formData.equipe} onChange={(e) => setFormData({ ...formData, equipe: e.target.value })} className="bg-white border-2 border-border rounded-sm h-12 px-4" />
                    </div>
                    
                    {/* Dynamic Custom Fields based on Event Type */}
                    {eventType?.custom_fields?.map((field) => (
                      <div key={field.field_key}>
                        <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
                          {field.name} {field.required && '*'}
                        </Label>
                        {field.field_type === 'select' ? (
                          <select
                            data-testid={`input-custom-${field.field_key}`}
                            value={formData.custom_fields?.[field.field_key] || ''}
                            onChange={(e) => setFormData({ ...formData, custom_fields: { ...formData.custom_fields, [field.field_key]: e.target.value } })}
                            className="w-full bg-white border-2 border-border rounded-sm h-12 px-4"
                          >
                            <option value="">Selecione</option>
                            {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        ) : (
                          <Input
                            data-testid={`input-custom-${field.field_key}`}
                            type={field.field_type === 'number' ? 'number' : 'text'}
                            placeholder={field.placeholder || ''}
                            value={formData.custom_fields?.[field.field_key] || ''}
                            onChange={(e) => setFormData({ ...formData, custom_fields: { ...formData.custom_fields, [field.field_key]: e.target.value } })}
                            className="bg-white border-2 border-border rounded-sm h-12 px-4"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4">
                    <Label className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Observações</Label>
                    <Textarea data-testid="input-observacoes" value={formData.observacoes} onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })} className="bg-white border-2 border-border rounded-sm px-4 min-h-20" placeholder="Alguma informação adicional que gostaria de nos informar?" />
                  </div>
                </div>

                {/* Optional Items */}
                {event?.optional_items && event.optional_items.length > 0 && (
                  <div data-testid="optional-items-section" className="bg-green-50 border-2 border-green-300 rounded-lg p-6">
                    <h3 className="text-lg font-bold text-green-700 mb-4 border-b border-green-300 pb-2">
                      Itens Adicionais (Opcionais)
                    </h3>
                    <p className="text-sm text-green-600 mb-4">
                      Aproveite e adicione itens extras à sua inscrição!
                    </p>
                    <div className="space-y-3">
                      {event.optional_items.filter(item => item.is_active).map((item) => {
                        const selectedItem = formData.selected_optional_items.find(i => i.item_id === item.id);
                        const quantity = selectedItem?.quantity || 0;
                        
                        return (
                          <div key={item.id} className="flex items-center justify-between bg-white p-4 rounded-lg border border-green-200">
                            <div className="flex-1">
                              <p className="font-bold text-foreground">{item.name}</p>
                              {item.description && <p className="text-sm text-foreground/60">{item.description}</p>}
                              <p className="text-green-700 font-bold">R$ {item.price.toFixed(2)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => handleOptionalItemChange(item, quantity - 1)}
                                disabled={quantity === 0}
                                className="h-8 w-8 p-0"
                              >
                                -
                              </Button>
                              <span className="w-8 text-center font-bold">{quantity}</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => handleOptionalItemChange(item, quantity + 1)}
                                disabled={quantity >= item.max_quantity}
                                className="h-8 w-8 p-0"
                              >
                                +
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {optionalItemsTotal > 0 && (
                      <div className="mt-4 pt-4 border-t border-green-300 text-right">
                        <p className="text-lg font-bold text-green-700">
                          Total Adicionais: R$ {optionalItemsTotal.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Terms */}
                <div className="space-y-4 bg-secondary/30 p-4 rounded-lg">
                  {event?.regulamento_mode === 'file' && event?.regulamento_file_url ? (
                    <button
                      type="button"
                      onClick={openRegulamentoFile}
                      className="flex items-center gap-2 text-sm font-bold text-accent hover:text-accent/80 underline"
                      data-testid="ver-regulamento-btn"
                    >
                      <FileText className="w-4 h-4" />
                      Ver Regulamento do Evento
                    </button>
                  ) : event?.regulamento ? (
                    <button
                      type="button"
                      onClick={() => setRegulamentoDialogOpen(true)}
                      className="flex items-center gap-2 text-sm font-bold text-accent hover:text-accent/80 underline"
                      data-testid="ver-regulamento-btn"
                    >
                      <FileText className="w-4 h-4" />
                      Ver Regulamento do Evento
                    </button>
                  ) : null}
                  <label className={`flex items-start gap-3 cursor-pointer ${fieldErrors.aceite_termos ? 'bg-red-50 p-2 rounded border border-red-300' : ''}`}>
                    <input type="checkbox" checked={formData.aceite_termos} onChange={(e) => { setFormData({ ...formData, aceite_termos: e.target.checked }); clearFieldError('aceite_termos'); }} className="w-5 h-5 mt-0.5" />
                    <span className={`text-sm ${fieldErrors.aceite_termos ? 'text-red-600 font-bold' : 'text-foreground/80'}`}>Li e aceito os termos e regulamento do evento. *</span>
                  </label>
                  <label className={`flex items-start gap-3 cursor-pointer ${fieldErrors.aceite_responsabilidade ? 'bg-red-50 p-2 rounded border border-red-300' : ''}`}>
                    <input type="checkbox" checked={formData.aceite_responsabilidade} onChange={(e) => { setFormData({ ...formData, aceite_responsabilidade: e.target.checked }); clearFieldError('aceite_responsabilidade'); }} className="w-5 h-5 mt-0.5" />
                    <span className={`text-sm ${fieldErrors.aceite_responsabilidade ? 'text-red-600 font-bold' : 'text-foreground/80'}`}>Declaro estar ciente dos riscos e assumo total responsabilidade. *</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={formData.aceite_imagem} onChange={(e) => setFormData({ ...formData, aceite_imagem: e.target.checked })} className="w-5 h-5 mt-0.5" />
                    <span className="text-sm text-foreground/80">Autorizo o uso de minha imagem em fotos e vídeos do evento.</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={formData.aceite_marketing} onChange={(e) => setFormData({ ...formData, aceite_marketing: e.target.checked })} className="w-5 h-5 mt-0.5" />
                    <span className="text-sm text-foreground/80">Desejo receber informações sobre produtos e eventos.</span>
                  </label>
                </div>

                <Button
                  data-testid="add-to-cart-button"
                  type="button"
                  onClick={addToCart}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-6 rounded-sm text-lg"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  {editingIndex !== null ? 'Atualizar Participante' : 'Adicionar ao Carrinho'}
                </Button>
              </div>
            </div>

            {/* Cart Section */}
            {showCart && (
              <RegistrationCart
                cart={registrationCart}
                events={[event]}
                currentPrice={getCurrentPrice()}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                cardForm={cardForm}
                setCardForm={setCardForm}
                billingData={billingData}
                setBillingData={setBillingData}
                billingParticipantIndex={billingParticipantIndex}
                setBillingParticipantIndex={setBillingParticipantIndex}
                onRemove={removeFromCart}
                onEdit={editCartItem}
                onCheckout={handleCheckout}
                onClear={clearCart}
                submitting={submitting}
                formatCPF={formatCPF}
                formatCNPJ={formatCNPJ}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price Card */}
            <div data-testid="price-card" className="bg-card border border-border rounded-lg p-6 sticky top-24">
              <div className="text-center mb-6">
                <p className="text-sm uppercase tracking-widest font-bold text-foreground/70">Inscrição</p>
                <p className="text-4xl font-heading font-black text-accent">R$ {getCurrentPrice().toFixed(2)}</p>
                {event?.current_lot && (
                  <p className="text-sm text-accent mt-1">{event.current_lot.name}</p>
                )}
              </div>

              {registrationCart.length > 0 && (
                <div className="mb-6 p-4 bg-accent/10 rounded-lg">
                  <p className="text-sm font-bold text-accent">No carrinho: {registrationCart.length} participante(s)</p>
                  {(() => {
                    const totalOptionalItems = registrationCart.reduce((sum, item) => sum + (item.optional_items_total || 0), 0);
                    const totalInscricao = registrationCart.length * getCurrentPrice();
                    const grandTotal = totalInscricao + totalOptionalItems;
                    return (
                      <>
                        {totalOptionalItems > 0 && (
                          <p className="text-xs text-accent/80">
                            Inscrições: R$ {totalInscricao.toFixed(2)} + Adicionais: R$ {totalOptionalItems.toFixed(2)}
                          </p>
                        )}
                        <p className="text-lg font-bold text-accent">Total: R$ {grandTotal.toFixed(2)}</p>
                      </>
                    );
                  })()}
                </div>
              )}

              <Button
                onClick={() => setShowCart(!showCart)}
                variant={showCart ? 'outline' : 'default'}
                className="w-full uppercase tracking-widest font-bold px-6 py-4 rounded-sm mb-4"
              >
                {showCart ? 'Ocultar Carrinho' : 'Ver Carrinho'}
              </Button>
              { /* Remaining Slots
              <p className="text-xs text-foreground/50 text-center">
                {event?.max_participants - event?.current_participants} vagas restantes
              </p>
               */}
            </div>

            {/* Price Lots */}
            <PriceLots priceLots={event?.price_lots} currentLot={event?.current_lot} />
          </div>
        </div>
      </div>

      <PixDialog open={pixDialogOpen} onOpenChange={setPixDialogOpen} pixData={pixData} />
      <BoletoDialog open={boletoDialogOpen} onOpenChange={setBoletoDialogOpen} boletoData={boletoData} />

      {/* Regulamento Dialog */}
      <Dialog open={regulamentoDialogOpen} onOpenChange={setRegulamentoDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">
              Regulamento do Evento
            </DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-wrap text-foreground/80 leading-relaxed">
              {event?.regulamento || 'Regulamento não disponível.'}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDetail;
